import { useState, useEffect, ChangeEvent, SyntheticEvent } from 'react'
import { useTheme } from '@mui/material/styles'
import {
    UpdateHcsPreferencesBodyIds,
    useGetIdsHealthcareservicesHcsIdPreferences,
    usePutIdsHealthcareservicesHcsIdPreferences,
    // useGetIdsHealthCareServicesHcsIdWorkflows,
} from '@toktokdoctech/tokapi-client'
import Paper from '@mui/material/Paper'
import Card from '@mui/material/Card'
import CardHeader from '@mui/material/CardHeader'
import CardContent from '@mui/material/CardContent'
import AppBar from '@mui/material/AppBar'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import useMediaQuery from '@mui/material/useMediaQuery'
import { Alert } from '@mui/material'
import { useNavigate } from 'react-router'
import TabPanel from '../utilsComponents/TabPanel'
import TokTokDocSaveButtons from '../Common/SaveButtons'
import { sxs } from './_styles'
import Loader from '../Common/Loader/Loader'
import { SwitchDisplay, TabsDisplay } from './_props'
import useUserPermissions from '../../hooks/useUserPermissions'
import useCurrentHcs from '../../hooks/useCurrentHcs'
import PreferenceGeneralTab from './preferencesGeneralTab/PreferenceGeneralTab'
import PreferencesSwitch from './preferencesGeneralTab/PreferencesGeneralTabSwitch'
import { useAppDispatch } from '../../store'
import { displaySnackbar } from '../../actions/main'

export default function Preferences() {
    const { hasUserPermission } = useUserPermissions()
    const navigate = useNavigate()
    const dispatch = useAppDispatch()

    /* ### STYLES ### */
    const theme = useTheme()
    const large = useMediaQuery(theme.breakpoints.up('sm'))

    /* ### STATE ### */
    const [value, setValue] = useState(0)
    const currentHcs = useCurrentHcs()
    const { data: preferences, isLoading: isLoadingPreferences } = useGetIdsHealthcareservicesHcsIdPreferences(
        currentHcs.id,
    )

    const [medicalMailProcess, setMedicalMailProcess] = useState('')
    const [admMailProcess, setAdmMailProcess] = useState('')
    const [isDirty, setIsDirty] = useState(false)

    const [switches, setSwitches] = useState({
        canForcePassword: preferences?.canForcePassword,
        soundAlert: preferences?.soundAlert,
        passwordRotationEnabled: preferences?.passwordRotationEnabled,
        automaticValidationDisabled: preferences?.isInsAutomaticValidationDisabled,
    })

    const tabsDisplay: TabsDisplay = {
        general: true,
        consultation: true,
        transmission: false,
    }
    const switchDisplay: SwitchDisplay = {
        automaticValidationDisabled: hasUserPermission('SUPER ADMIN'),
    }

    const mutationOptions = {
        onSuccess: () => {
            setIsDirty(false)
            navigate('/')
        },
        onError: (err: any) => {
            console.error('Failed to save preferences', err)
            setIsDirty(true)
            dispatch(displaySnackbar('error', 'Une erreur est survenue lors de la sauvegarde des préférences.'))
        },
    }

    const mutation = usePutIdsHealthcareservicesHcsIdPreferences({
        mutation: mutationOptions,
    })

    useEffect(() => {
        if (!preferences) return

        setSwitches({
            canForcePassword: preferences.canForcePassword,
            soundAlert: preferences.soundAlert,
            passwordRotationEnabled: preferences.passwordRotationEnabled,
            automaticValidationDisabled: preferences.isInsAutomaticValidationDisabled,
        })
        setMedicalMailProcess(preferences.medicalMailProcOverride || 'Both')
        setAdmMailProcess(preferences.admMailProcOverride || 'Both')
    }, [preferences])

    const handleSwitchesChange = (event: ChangeEvent<HTMLInputElement>, newValue: boolean) => {
        const { name } = event.target
        setSwitches({
            ...switches,
            [name]: newValue,
        })
        setIsDirty(true)
    }

    const handleChange = (event: SyntheticEvent<Element, Event>, newValue: number) => {
        setValue(newValue)
    }

    const handleMedicalProcessChange = (event: ChangeEvent<HTMLInputElement>) => {
        const { value: newValue } = event.target
        setMedicalMailProcess(newValue)
    }

    const handleAdmProcessChange = (event: ChangeEvent<HTMLInputElement>) => {
        const { value: newValue } = event.target
        setAdmMailProcess(newValue)
    }

    const changeTab = (index: number) => ({
        id: `scrollable-auto-tab-${index}`,
        'aria-controls': `scrollable-austo-tabpanel-${index}`,
    })

    const savePreferences = async () => {
        const data: UpdateHcsPreferencesBodyIds = {
            canForcePassword: switches.canForcePassword === true,
            hasSoundAlert: switches.soundAlert === true,
            hasPasswordRotationEnabled: switches.passwordRotationEnabled === true,
            isInsAutomaticValidationDisabled: switches.automaticValidationDisabled === true,
            medicalMailProcOverride: medicalMailProcess === 'Both' ? undefined : medicalMailProcess,
            admMailProcOverride: admMailProcess === 'Both' ? undefined : admMailProcess,
        }

        mutation.mutate({
            hcsId: currentHcs.id,
            data,
        })
    }

    if (isLoadingPreferences) {
        return (
            <Paper sx={sxs.paper}>
                <Loader title="Chargement des préférences..." />
            </Paper>
        )
    }

    if (Object.values(tabsDisplay).filter((x) => x === true).length === 0) {
        return (
            <Paper sx={sxs.paper}>
                <Alert severity="error" sx={sxs.alertBoxContainer}>
                    Erreur: aucune préférence disponible
                </Alert>
            </Paper>
        )
    }

    return (
        <Paper sx={sxs.paper}>
            <Card>
                <CardHeader
                    style={{
                        textAlign: 'left',
                        paddingLeft: '10px',
                        paddingBottom: '0px',
                    }}
                    title="Préférences établissement"
                />
                <CardContent>
                    <AppBar position="static" color="default" sx={sxs.appBar}>
                        <Tabs
                            value={value}
                            onChange={handleChange}
                            indicatorColor="primary"
                            textColor="primary"
                            variant="fullWidth"
                            scrollButtons="auto"
                            aria-label="scrollable auto tabs example"
                        >
                            {tabsDisplay.general && <Tab sx={sxs.tabsLabel} label="Général" {...changeTab(0)} />}
                            {tabsDisplay.consultation && (
                                <Tab sx={sxs.tabsLabel} label={large ? 'Téléconsultation' : 'TLC'} {...changeTab(1)} />
                            )}
                            {tabsDisplay.transmission && (
                                <Tab sx={sxs.tabsLabel} label={large ? 'Téléexpertise' : 'TLE'} {...changeTab(2)} />
                            )}
                        </Tabs>
                    </AppBar>
                    {tabsDisplay.general && (
                        <PreferenceGeneralTab
                            value={value}
                            index={0}
                            switches={switches}
                            handleSwitchesChange={handleSwitchesChange}
                            switchDisplay={switchDisplay}
                            medicalMailProcess={medicalMailProcess}
                            handleMedicalProcessChange={handleMedicalProcessChange}
                            admMailProcess={admMailProcess}
                            handleAdmProcessChange={handleAdmProcessChange}
                        />
                    )}
                    {tabsDisplay.consultation && (
                        <TabPanel value={value} index={1}>
                            <PreferencesSwitch
                                checked={switches.soundAlert}
                                onChange={handleSwitchesChange}
                                name="soundAlert"
                                label="Alerte sonore activée"
                            />
                        </TabPanel>
                    )}
                    <TokTokDocSaveButtons
                        onSave={savePreferences}
                        allowedRoles="ADMIN"
                        shouldBlockNavigation={() => isDirty}
                    />
                </CardContent>
            </Card>
        </Paper>
    )
}
